import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUser as useClerkUser } from '@clerk/clerk-react'
import { api } from '@/lib/api'
import type { UserResponse } from '@/types'

export function useUserProfile() {
  const { user: clerkUser, isLoaded, isSignedIn } = useClerkUser()

  const query = useQuery({
    queryKey: ['user', clerkUser?.id],
    queryFn: async () => {
      if (!clerkUser?.id) throw new Error('No user ID')
      const response = await api.get<UserResponse>(`/users/${clerkUser.id}`)
      return response
    },
    enabled: isLoaded && isSignedIn && !!clerkUser?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    ...query,
    user: query.data?.user ?? null,
    creditGrant: query.data?.credit_grant_applied ?? null,
    clerkUser,
    isLoaded,
    isSignedIn,
    userId: clerkUser?.id ?? null,
  }
}

export function useSetFingerprint() {
  const { user: clerkUser } = useClerkUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (fingerprint: string) => {
      if (!clerkUser?.id) throw new Error('No user ID')
      return api.put<{ success: boolean }>(`/users/${clerkUser.id}/fingerprint`, {
        fingerprint,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', clerkUser?.id] })
    },
  })
}

export function useCheckFingerprint(fingerprint: string | null) {
  return useQuery({
    queryKey: ['fingerprint', fingerprint],
    queryFn: async () => {
      if (!fingerprint) throw new Error('No fingerprint')
      return api.get<{ exists: boolean; user_id?: string }>(`/fingerprints/${fingerprint}`)
    },
    enabled: !!fingerprint,
  })
}
